import React, { useState } from 'react';
import styled from 'styled-components';
import Section from './Section';
import Container from './Container';
import Button from './Button';
import Botpoison from '@botpoison/browser';

// Background image from Unsplash
const backgroundImage = 'https://images.unsplash.com/photo-1593113616828-6f22bca04804?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const ContactSection = styled(Section)`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    opacity: 0.05;
    z-index: 0;
  }
`;

const ContactContent = styled(Container)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  min-width: 300px;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    text-align: center;
    min-width: 100%;
  }
`;

const FormContainer = styled.div`
  flex: 2;
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md};
  padding: ${props => props.theme.spacing.xl};
  min-width: 300px;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    min-width: 100%;
  }
`;

const Title = styled.h2`
  position: relative;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 0;
    width: 80px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
    
    @media (max-width: ${props => props.theme.breakpoints.lg}) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const Description = styled.p`
  margin-bottom: ${props => props.theme.spacing.lg};
  line-height: 1.7;
  font-size: ${props => props.theme.fontSizes.md};
`;

const ContactDetail = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
`;

const Icon = styled.div`
  margin-right: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
`;

const ContactText = styled.span`
  font-size: ${props => props.theme.fontSizes.md};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  font-size: ${props => props.theme.fontSizes.md};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  transition: ${props => props.theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(40, 126, 60, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  min-height: 150px;
  resize: vertical;
  transition: ${props => props.theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(40, 126, 60, 0.2);
  }
`;

const SubmitButton = styled(Button)`
  align-self: flex-start;
  margin-top: ${props => props.theme.spacing.sm};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.md};
  text-align: center;
  font-weight: ${props => props.theme.fontWeights.semiBold};
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize Botpoison
  const botpoison = new Botpoison({
    publicKey: "pk_f1f8d63e-2bfd-4ea4-9809-c396cd9c4fd7"
  });

  const validate = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      email: '',
      subject: '',
      message: ''
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await fetch('https://submit-form.com/LE5mKJNPJ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _botpoison: solution
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <ContactContent>
        <ContactInfo>
          <Title>Contact Us</Title>
          <Description>
            Have questions about our projects or want to get involved? Reach out to us using the form or through our contact information below.
          </Description>

          <ContactDetail>
            <Icon>✉️</Icon>
            <ContactText>admin@project-assist.net</ContactText>
          </ContactDetail>

          <ContactDetail>
            <Icon>📍</Icon>
            <ContactText>Bay Area, California</ContactText>
          </ContactDetail>

          <ContactDetail>
            <Icon>🏢</Icon>
            <ContactText>EIN: 88-1941937</ContactText>
          </ContactDetail>
        </ContactInfo>

        <FormContainer>
          {submitStatus === 'success' ? (
            <SuccessMessage>
              Thank you for your message! We'll get back to you as soon as possible.
            </SuccessMessage>
          ) : submitStatus === 'error' ? (
            <ErrorMessage>
              Sorry, there was an error sending your message. Please try again or contact us directly.
            </ErrorMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>

              <SubmitButton
                variant="primary"
                size="large"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </Form>
          )}
        </FormContainer>
      </ContactContent>
    </ContactSection>
  );
};

export default Contact; 